import { useEffect, useState, useCallback } from "react";
import { X, Download, Share } from "lucide-react";
import { useLocale } from "../context/LocaleContext";

const DISMISS_KEY = "vfl_pwa_install_dismissed_at";
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days
const SHOW_DELAY_MS = 4000;

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

function isDismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < DISMISS_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent || "");
}

function isSafari() {
  const ua = window.navigator.userAgent || "";
  return /safari/i.test(ua) && !/crios|fxios|chrome|android/i.test(ua);
}

/**
 * Promotes installing the PWA for faster access. On Chrome/Edge/Android it
 * captures the native beforeinstallprompt event and triggers it on tap. iOS
 * Safari never fires that event, so there we show manual "Share > Add to
 * Home Screen" instructions instead — that's the only install path Apple
 * allows.
 */
export default function PWAInstallPrompt() {
  const { t } = useLocale();
  const [deferredEvent, setDeferredEvent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosInstructions, setShowIosInstructions] = useState(false);

  useEffect(() => {
    if (isStandalone() || isDismissedRecently()) return;

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredEvent(e);
      setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);

    let iosTimer;
    if (isIos() && isSafari()) {
      iosTimer = setTimeout(() => {
        setShowIosInstructions(true);
        setVisible(true);
      }, SHOW_DELAY_MS);
    }

    const onInstalled = () => setVisible(false);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredEvent) return;
    deferredEvent.prompt();
    await deferredEvent.userChoice;
    setDeferredEvent(null);
    dismiss();
  }, [deferredEvent, dismiss]);

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-3 left-3 right-3 sm:left-auto sm:right-5 sm:bottom-5 sm:w-96 z-[9998] bg-white border border-slate-200 rounded-2xl shadow-2xl p-4 flex items-start gap-3 animate-in fade-in slide-in-from-bottom-4"
      data-testid="pwa-install-banner"
    >
      <img
        src="/android-chrome-192x192.png"
        alt="Ventura Fresh Laundry"
        className="w-12 h-12 rounded-xl shrink-0"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-slate-900">
          {t("Install the app", "Instala la app")}
        </p>
        <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
          {showIosInstructions
            ? t(
                "Tap Share, then \"Add to Home Screen\" for faster access.",
                "Toca Compartir y luego \"Agregar a inicio\" para acceso más rápido."
              )
            : t(
                "Add Ventura Fresh Laundry to your home screen for faster access.",
                "Agrega Ventura Fresh Laundry a tu pantalla de inicio para un acceso más rápido."
              )}
        </p>
        {!showIosInstructions && (
          <button
            onClick={handleInstall}
            className="mt-3 inline-flex items-center gap-1.5 bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold rounded-full px-4 py-2 transition-colors"
            data-testid="pwa-install-btn"
          >
            <Download className="w-3.5 h-3.5" />
            {t("Install", "Instalar")}
          </button>
        )}
        {showIosInstructions && (
          <div className="mt-2 inline-flex items-center gap-1.5 text-slate-400 text-[11px]">
            <Share className="w-3.5 h-3.5" />
            {t("Share icon → Add to Home Screen", "Ícono Compartir → Agregar a inicio")}
          </div>
        )}
      </div>
      <button
        onClick={dismiss}
        className="shrink-0 w-7 h-7 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center"
        data-testid="pwa-install-dismiss"
        aria-label={t("Dismiss", "Cerrar")}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
